import { EOnboardingKey, ESubVerticalUNI } from '@constants';
import { IUser, IUserInfo } from '@sdk/contracts';
import { Flex, Form, FormInstance } from 'antd';
import { useImperativeHandle, forwardRef } from 'react';

import { FpFormContent } from '../FpFormContent';
import { LoadingCircleSpinner } from '../Loaders';
import { OposFormContent } from '../OposFormContent';
import { PceFormContent } from '../PceFormContent';
import { UniFormContent } from '../UniFormContent';

import { useProfile, useAcademy } from '@/store';
import { useUserInfoByUser } from '@/store/users/user-info';
import { getUserSubverticals } from '@/utils/getUserSubverticals';

type Props = {
  data?: IUser;
  form: FormInstance<IUser>;
};
export interface StudentTrainingInfoFormRef {
  getUserInfoValues: () => IUserInfo | null;
  isUserInfoTouched: () => boolean;
}

export const StudentTrainingInfoForm = forwardRef<StudentTrainingInfoFormRef, Props>(
  ({ data }, ref) => {
    const academyId = data?.academy?._id;

    const { academy: academyData } = useAcademy(academyId);

    const [userInfoForm] = Form.useForm<IUserInfo>();
    const {
      roleCheck: { isManager }
    } = useProfile();
    const { userInfo, isPending } = useUserInfoByUser(data?._id);

    useImperativeHandle(ref, () => ({
      getUserInfoValues: () => {
        if (!userInfo) return null;
        const values = userInfoForm.getFieldsValue();
        return values;
      },
      isUserInfoTouched: () => {
        const touched = userInfoForm.isFieldsTouched();
        return touched;
      }
    }));

    if (isPending) {
      return (
        <Flex justify="center" align="center">
          <LoadingCircleSpinner />
        </Flex>
      );
    }
    if (!userInfo) return null;

    const onboardingKey = academyData?.onboarding?.onboardingKey;
    const userSubverticals = getUserSubverticals(data);

    const content = (() => {
      switch (onboardingKey) {
        case EOnboardingKey.FP:
          return <FpFormContent isManager={isManager} profile={data} />;

        case EOnboardingKey.OPOS:
          return <OposFormContent isManager={isManager} form={userInfoForm} />;

        case EOnboardingKey.UNI:
          if (userSubverticals.includes(ESubVerticalUNI.PCE)) {
            return <PceFormContent isManager={isManager} form={userInfoForm} />;
          }
          return <UniFormContent isManager={isManager} form={userInfoForm} profile={data} />;

        default:
          return <UniFormContent isManager={isManager} form={userInfoForm} profile={data} />;
      }
    })();

    return (
      <Form<IUserInfo> layout="vertical" form={userInfoForm} initialValues={userInfo}>
        {content}
      </Form>
    );
  }
);

StudentTrainingInfoForm.displayName = 'StudentTrainingInfoForm';
